import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4001';

function AddSilkscreenModal({ onClose, onSuccess, customers, onAddCustomer, preSelectedCustomerId }) {
  const [formData, setFormData] = useState({
    screenCode: '',
    currentCustomerName: '',
    currentItemDescription: '',
    amountAcross: '',
    amountAround: '',
    dateReceived: new Date().toISOString().split('T')[0]
  });
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [artworkFile, setArtworkFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (preSelectedCustomerId && customers.length > 0) {
      const customer = customers.find(c => c.id === preSelectedCustomerId);
      if (customer) {
        setSelectedCustomerId(preSelectedCustomerId.toString());
        setFormData(prev => ({ ...prev, currentCustomerName: customer.name }));
      }
    }
  }, [preSelectedCustomerId, customers]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCustomerChange = (e) => {
    const value = e.target.value;
    if (value === 'ADD_NEW') {
      onAddCustomer();
      return;
    }
    setSelectedCustomerId(value);
    const customer = customers.find(c => c.id === parseInt(value));
    if (customer) {
      setFormData(prev => ({ ...prev, currentCustomerName: customer.name }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 4 * 1024 * 1024) {
        setError('File size must be less than 4MB');
        return;
      }
      if (file.type !== 'application/pdf') {
        setError('Only PDF files are allowed');
        return;
      }
      setArtworkFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    console.log('Form data:', formData);
    console.log('Selected customer ID:', selectedCustomerId);

    if (!formData.screenCode || !selectedCustomerId || !formData.currentItemDescription || 
        !formData.amountAcross || !formData.amountAround || !formData.dateReceived) {
      setError('All fields are required');
      return;
    }

    if (parseInt(formData.amountAcross) <= 0 || parseInt(formData.amountAround) <= 0) {
      setError('Amount across and around must be greater than 0');
      return;
    }

    setLoading(true);

    try {
      // Ensure customer name is set from selected customer if not already
      let customerName = formData.currentCustomerName;
      if (!customerName && selectedCustomerId) {
        const customer = customers.find(c => c.id === selectedCustomerId);
        if (customer) {
          customerName = customer.name;
        }
      }

      console.log('Sending customer name:', customerName);

      const formDataToSend = new FormData();
      formDataToSend.append('screenCode', formData.screenCode);
      formDataToSend.append('currentCustomerName', customerName);
      formDataToSend.append('currentItemDescription', formData.currentItemDescription);
      formDataToSend.append('amountAcross', formData.amountAcross);
      formDataToSend.append('amountAround', formData.amountAround);
      formDataToSend.append('dateReceived', formData.dateReceived);
      
      if (artworkFile) {
        formDataToSend.append('artworkPdf', artworkFile);
      }

      const response = await fetch(`${API_URL}/silkscreens`, {
        method: 'POST',
        body: formDataToSend
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create silkscreen');
      }

      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '32px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
      }}>
        <h2 style={{
          fontSize: '1.8rem',
          fontWeight: '700',
          color: '#00BCD4',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          🖼️ Add New Silkscreen
        </h2>

        <form onSubmit={handleSubmit}>
          {/* Screen Code */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontWeight: '600',
              marginBottom: '8px',
              color: '#333'
            }}>
              Screen Code *
            </label>
            <input
              type="text"
              name="screenCode"
              value={formData.screenCode}
              onChange={handleChange}
              placeholder="e.g., SS-001"
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '1rem',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#00BCD4'}
              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
            />
          </div>

          {/* Date Received */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontWeight: '600',
              marginBottom: '8px',
              color: '#333'
            }}>
              Date Received *
            </label>
            <input
              type="date"
              name="dateReceived"
              value={formData.dateReceived}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '1rem',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#00BCD4'}
              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
            />
          </div>

          {/* Customer Name */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontWeight: '600',
              marginBottom: '8px',
              color: '#333'
            }}>
              Customer Name *
            </label>
            <select
              value={selectedCustomerId}
              onChange={handleCustomerChange}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '1rem',
                transition: 'border-color 0.2s',
                cursor: 'pointer'
              }}
              onFocus={(e) => e.target.style.borderColor = '#00BCD4'}
              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
            >
              <option value="">Select a customer...</option>
              {customers && customers.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {customer.name}
                </option>
              ))}
              <option value="ADD_NEW" style={{ fontWeight: '700', color: '#00BCD4' }}>
                + Add New Customer
              </option>
            </select>
          </div>

          {/* Item Description */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontWeight: '600',
              marginBottom: '8px',
              color: '#333'
            }}>
              Item Description *
            </label>
            <textarea
              name="currentItemDescription"
              value={formData.currentItemDescription}
              onChange={handleChange}
              placeholder="e.g., Logo Print - Red"
              rows="3"
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '1rem',
                fontFamily: 'inherit',
                resize: 'vertical',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#00BCD4'}
              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
            />
          </div>

          {/* Layout */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px',
            marginBottom: '20px'
          }}>
            <div>
              <label style={{
                display: 'block',
                fontWeight: '600',
                marginBottom: '8px',
                color: '#333'
              }}>
                Amount Across *
              </label>
              <input
                type="number"
                name="amountAcross"
                value={formData.amountAcross}
                onChange={handleChange}
                min="1"
                placeholder="e.g., 2"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#00BCD4'}
                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
              />
            </div>
            <div>
              <label style={{
                display: 'block',
                fontWeight: '600',
                marginBottom: '8px',
                color: '#333'
              }}>
                Amount Around *
              </label>
              <input
                type="number"
                name="amountAround"
                value={formData.amountAround}
                onChange={handleChange}
                min="1"
                placeholder="e.g., 2"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#00BCD4'}
                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
              />
            </div>
          </div>

          {formData.amountAcross && formData.amountAround && (
            <div style={{
              background: '#E0F7FA',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '20px',
              color: '#00838F',
              fontSize: '0.9rem'
            }}>
              Total Positions: {parseInt(formData.amountAcross) * parseInt(formData.amountAround)}
            </div>
          )}

          {/* Artwork Upload */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontWeight: '600',
              marginBottom: '8px',
              color: '#333'
            }}>
              Artwork PDF (Max 4MB)
            </label>
            <input
              type="file"
              accept="application/pdf"
              onChange={handleFileChange}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px dashed #00BCD4',
                borderRadius: '8px',
                fontSize: '1rem',
                cursor: 'pointer'
              }}
            />
            {artworkFile && (
              <div style={{
                marginTop: '12px',
                padding: '12px',
                background: '#E8F5E9',
                borderRadius: '8px',
                color: '#2E7D32',
                fontSize: '0.9rem'
              }}>
                ✓ {artworkFile.name} ({(artworkFile.size / 1024 / 1024).toFixed(2)} MB)
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div style={{
              background: '#FFEBEE',
              color: '#C62828',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '0.9rem'
            }}>
              {error}
            </div>
          )}

          {/* Buttons */}
          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '12px 24px',
                border: '2px solid #e0e0e0',
                background: 'white',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.5 : 1
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '12px 24px',
                border: 'none',
                background: loading ? '#ccc' : 'linear-gradient(135deg, #00BCD4 0%, #0097A7 100%)',
                color: 'white',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: loading ? 'none' : '0 4px 12px rgba(0, 188, 212, 0.3)'
              }}
            >
              {loading ? 'Adding...' : 'Add Silkscreen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddSilkscreenModal;
